import React, { useRef, useState } from 'react';
import { Star, ChevronLeft, ChevronRight, ExternalLink, Quote, PenLine } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { GOOGLE_REVIEWS, GOOGLE_REVIEWS_URL, GOOGLE_WRITE_REVIEW_URL, reviewsTranslations } from '../translations/reviews';

const Stars = ({ size = 18 }) => (
  <div className="flex items-center gap-0.5 text-yellow-500" aria-label="5/5">
    {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={size} className="fill-current" />)}
  </div>
);

const GoogleG = () => (
  <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.9 6.1C12.5 13.6 17.8 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.6 5.9c4.4-4.1 7-10.1 7-17.6z"/>
    <path fill="#FBBC05" d="M10.6 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.7 10.7l7.9-6.1z"/>
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2.1 1.4-4.9 2.3-8.3 2.3-6.2 0-11.5-4.2-13.4-9.9l-7.9 6.1C6.6 42.6 14.6 48 24 48z"/>
  </svg>
);

const ReviewCard = ({ review, index }) => (
  <article
    data-testid={`google-review-card-${index}`}
    className="snap-start shrink-0 w-[85%] sm:w-[60%] lg:w-[calc((100%-3rem)/3)] flex flex-col bg-white rounded-2xl border border-gray-100 shadow-lg p-7 md:p-8"
  >
    <div className="flex items-center justify-between mb-5">
      <Stars />
      <GoogleG />
    </div>
    <Quote size={28} className="text-teal-100 mb-3" />
    <p className="text-gray-700 leading-relaxed italic flex-1">« {review.text} »</p>
    <div className="mt-6 pt-5 border-t border-gray-100 flex items-center gap-3">
      <span className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-pink-500 text-white font-semibold flex items-center justify-center">
        {review.name.charAt(0)}
      </span>
      <span data-testid={`google-review-name-${index}`} className="text-gray-900 font-semibold">{review.name}</span>
    </div>
  </article>
);

export const GoogleReviews = () => {
  const { language } = useLanguage();
  const t = reviewsTranslations[language];
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const scrollToIndex = (i) => {
    const track = trackRef.current;
    const card = track?.children[i];
    if (card) track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: 'smooth' });
  };

  const onScroll = () => {
    const track = trackRef.current;
    const cards = [...track.children];
    const closest = cards.reduce((best, c, i) =>
      Math.abs(c.offsetLeft - track.offsetLeft - track.scrollLeft) < Math.abs(cards[best].offsetLeft - track.offsetLeft - track.scrollLeft) ? i : best, 0);
    setActive(closest);
  };

  const go = (dir) => scrollToIndex(Math.min(Math.max(active + dir, 0), GOOGLE_REVIEWS.length - 1));

  return (
    <section data-testid="google-reviews-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col items-center text-center mb-12">
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-gray-500 mb-3">
            <GoogleG /> {t.eyebrow}
          </span>
          <h2 className="text-3xl md:text-4xl font-light text-teal-600 mb-4">{t.title}</h2>
          <div data-testid="google-reviews-rating" className="flex items-center gap-3 text-gray-700">
            <span className="text-2xl font-semibold text-gray-900">{t.rating}</span>
            <Stars size={20} />
            <span className="text-sm text-gray-500">· {t.count}</span>
          </div>
        </div>

        <div className="relative">
          <div
            ref={trackRef}
            onScroll={onScroll}
            data-testid="google-reviews-track"
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {GOOGLE_REVIEWS.map((r, i) => <ReviewCard key={r.name} review={r} index={i} />)}
          </div>
          <button type="button" onClick={() => go(-1)} aria-label={t.prev} data-testid="google-reviews-prev"
            className="hidden md:flex absolute -left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-lg border border-gray-100 items-center justify-center text-teal-600 hover:bg-teal-50 transition-colors">
            <ChevronLeft size={22} />
          </button>
          <button type="button" onClick={() => go(1)} aria-label={t.next} data-testid="google-reviews-next"
            className="hidden md:flex absolute -right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-lg border border-gray-100 items-center justify-center text-teal-600 hover:bg-teal-50 transition-colors">
            <ChevronRight size={22} />
          </button>
        </div>

        <div className="flex justify-center gap-2 mt-6">
          {GOOGLE_REVIEWS.map((r, i) => (
            <button key={r.name} type="button" onClick={() => scrollToIndex(i)} aria-label={`${i + 1}`} data-testid={`google-reviews-dot-${i}`}
              className={`h-2 rounded-full transition-[width,background-color] duration-300 ${active === i ? 'w-6 bg-teal-600' : 'w-2 bg-gray-300'}`} />
          ))}
        </div>

        <div className="flex flex-col items-center gap-4 mt-10">
          <a
            href={GOOGLE_REVIEWS_URL || undefined}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-all-button"
            aria-disabled={!GOOGLE_REVIEWS_URL}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full border border-teal-600 text-teal-700 font-medium transition-colors hover:bg-teal-50 ${GOOGLE_REVIEWS_URL ? '' : 'pointer-events-none opacity-60'}`}
          >
            {t.button} <ExternalLink size={16} />
          </a>
          <a
            href={GOOGLE_WRITE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-share-button"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-teal-600 underline-offset-4 hover:underline transition-colors"
          >
            <PenLine size={14} /> {t.share}
          </a>
        </div>
      </div>
    </section>
  );
};
